import { useMemo, useState } from "react";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";

import workspaceService from "api/workspaceService";
import {
  IWorkspace,
  IWorkspaceMember,
  IWorkspaceStructure,
} from "dto/IWorkspace";
import { IError } from "dto/IError";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Struktura organizacyjna firmy — JEDNO źródło prawdy: BE.
//  • komórki = WorkspaceStructureItems (nazwa, symbol do sygnatury JRWA, kierownik, rodzic),
//  • osoby   = WorkspaceMembers (członkowie zespołu) przypięci do komórki przez StructureId
//              (POST /workspace/member/move) i ze stanowiskiem (PUT /workspace/member).
// Zastępuje dawne drzewa z localStorage (team_structure_v1 / org_chart_v5), w których
// istniała osobna, sztuczna lista pracowników.

interface INode extends IWorkspaceStructure {
  children: INode[];
  depth: number;
}

const buildTree = (items: IWorkspaceStructure[]): INode[] => {
  const map = new Map<string, INode>();
  for (const i of items) map.set(i.Id, { ...i, children: [], depth: 0 });
  const roots: INode[] = [];
  for (const n of map.values()) {
    const parent = n.ParentId ? map.get(n.ParentId) : undefined;
    if (parent) parent.children.push(n);
    else roots.push(n);
  }
  const collator = new Intl.Collator("pl");
  const sortRec = (nodes: INode[], depth: number) => {
    nodes.sort((a, b) => collator.compare(a.Name, b.Name));
    for (const n of nodes) {
      n.depth = depth;
      sortRec(n.children, depth + 1);
    }
  };
  sortRec(roots, 0);
  return roots;
};

const flatten = (nodes: INode[], out: INode[] = []): INode[] => {
  for (const n of nodes) {
    out.push(n);
    flatten(n.children, out);
  }
  return out;
};

const descendantIds = (node: INode, out = new Set<string>()): Set<string> => {
  out.add(node.Id);
  node.children.forEach((c) => descendantIds(c, out));
  return out;
};

const errorText = (error: IError, fallback: string) =>
  error?.response?.data?.errorCodes?.[0]?.message ?? fallback;

const isActiveMember = (m: IWorkspaceMember) =>
  m.Status === 4 || m.Status === 1;

interface Props {
  workspace: IWorkspace;
  refetch: () => void;
  canEditStructure: boolean;
  canEditMembers: boolean;
}

const StructureEditor = ({
  workspace,
  refetch,
  canEditStructure,
  canEditMembers,
}: Props) => {
  const structure = useMemo(() => workspace?.Structure ?? [], [workspace]);
  const members = useMemo(
    () => (workspace?.Members ?? []).filter(isActiveMember),
    [workspace]
  );

  const tree = useMemo(() => buildTree(structure), [structure]);
  const flat = useMemo(() => flatten(tree), [tree]);

  const { mutate: createUnit, isLoading: isCreating } =
    workspaceService.useCreateStructure();
  const { mutate: updateUnit, isLoading: isUpdating } =
    workspaceService.useUpdateStructure();
  const { mutate: removeUnit } = workspaceService.useRemoveStructure();
  const { mutate: moveUnit } = workspaceService.useMoveStructure();
  const { mutate: moveMember } = workspaceService.useMoveMembershipStructure();
  const { mutate: editMember } = workspaceService.useWorkspaceEditMember();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Formularz komórki (edycja zaznaczonej albo nowa)
  const [editing, setEditing] = useState<{
    id: string | null;
    parentId: string | null;
    name: string;
    symbol: string;
    managerUserId: string;
  } | null>(null);

  // Przypisywanie osoby do zaznaczonej komórki
  const [memberToAssign, setMemberToAssign] = useState<IWorkspaceMember | null>(
    null
  );
  // Edycja stanowiska osoby
  const [positionEdit, setPositionEdit] = useState<{
    memberId: string;
    value: string;
  } | null>(null);

  const selected = flat.find((n) => n.Id === selectedId) ?? null;
  const unitMembers = selected
    ? members.filter((m) => m.StructureId === selected.Id)
    : [];
  const unassigned = members.filter(
    (m) => !m.StructureId || !flat.some((n) => n.Id === m.StructureId)
  );
  const memberLabel = (userId?: string | null) =>
    members.find((m) => m.UserId === userId)?.Label ?? null;

  const fail = (title: string, fallback: string) => (error: IError) =>
    notification({
      type: ENotificationType.ERROR,
      title,
      text: errorText(error, fallback),
    });

  // ── Komórki ──────────────────────────────────────────────────────────────

  const startCreate = (parentId: string | null) =>
    setEditing({ id: null, parentId, name: "", symbol: "", managerUserId: "" });

  const startEdit = (n: INode) =>
    setEditing({
      id: n.Id,
      parentId: n.ParentId,
      name: n.Name,
      symbol: n.Symbol ?? "",
      managerUserId: n.ManagerUserId ?? "",
    });

  const saveUnit = () => {
    if (!editing || !editing.name.trim()) return;
    const common = {
      Name: editing.name.trim(),
      Symbol: editing.symbol.trim() || null,
      ManagerUserId: editing.managerUserId || null,
    };
    if (editing.id) {
      updateUnit(
        { Id: editing.id, ...common },
        {
          onSuccess: () => {
            setEditing(null);
            refetch();
          },
          onError: fail("Struktura", "Nie udało się zapisać komórki"),
        }
      );
    } else {
      createUnit(
        { WorkspaceId: workspace.Id, ParentId: editing.parentId, ...common },
        {
          onSuccess: (r) => {
            setEditing(null);
            setSelectedId(r.data?.Id ?? null);
            refetch();
          },
          onError: fail("Struktura", "Nie udało się utworzyć komórki"),
        }
      );
    }
  };

  const handleRemove = (n: INode) => {
    if (!window.confirm(`Usunąć komórkę „${n.Name}"?`)) return;
    removeUnit(
      { Id: n.Id },
      {
        onSuccess: () => {
          if (selectedId === n.Id) setSelectedId(null);
          refetch();
        },
        onError: fail("Struktura", "Nie udało się usunąć komórki"),
      }
    );
  };

  const handleMoveUnit = (n: INode, parentId: string | null) => {
    moveUnit(
      { Id: n.Id, ParentId: parentId },
      {
        onSuccess: refetch,
        onError: fail("Struktura", "Nie udało się przenieść komórki"),
      }
    );
  };

  // ── Osoby ─────────────────────────────────────────────────────────────────

  const assignMember = (m: IWorkspaceMember, structureId: string | null) => {
    moveMember(
      { Id: m.Id, ParentId: structureId },
      {
        onSuccess: () => {
          setMemberToAssign(null);
          refetch();
        },
        onError: fail("Struktura", "Nie udało się przypisać osoby"),
      }
    );
  };

  const savePosition = () => {
    if (!positionEdit) return;
    editMember(
      { Id: positionEdit.memberId, PositionTitle: positionEdit.value.trim() },
      {
        onSuccess: () => {
          setPositionEdit(null);
          refetch();
        },
        onError: fail("Struktura", "Nie udało się zapisać stanowiska"),
      }
    );
  };

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // ── Render ────────────────────────────────────────────────────────────────

  const renderNode = (n: INode) => {
    const nodeMembers = members
      .filter((m) => m.StructureId === n.Id)
      .sort((a, b) => a.Label.localeCompare(b.Label, "pl"));
    const count = nodeMembers.length;
    const isCollapsed = collapsed.has(n.Id);
    const expandable = n.children.length > 0 || count > 0;
    return (
      <li key={n.Id}>
        <div
          className={`${styles.nodeRow} ${
            selectedId === n.Id ? styles.nodeRowSelected : ""
          }`}
          style={{ paddingLeft: 6 + n.depth * 18 }}
          onClick={() => {
            setSelectedId(n.Id);
            setEditing(null);
            setMemberToAssign(null);
          }}
        >
          <button
            className={styles.toggleBtn}
            style={{ visibility: expandable ? "visible" : "hidden" }}
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse(n.Id);
            }}
          >
            {isCollapsed ? "▸" : "▾"}
          </button>
          <span className={styles.nodeName}>
            {n.Symbol ? (
              <span className={styles.symbolChip}>{n.Symbol}</span>
            ) : null}
            {n.Name}
            <span className={styles.countChip}>{count}</span>
          </span>
          {canEditStructure && (
            <span className={styles.nodeActions}>
              <Tooltip title="Dodaj komórkę podrzędną">
                <button
                  className={styles.addChildBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(n.Id);
                    startCreate(n.Id);
                  }}
                >
                  +
                </button>
              </Tooltip>
              <Tooltip title="Edytuj">
                <button
                  className={styles.editBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(n.Id);
                    startEdit(n);
                  }}
                >
                  ✎
                </button>
              </Tooltip>
              <Tooltip title="Usuń">
                <button
                  className={styles.removeBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(n);
                  }}
                >
                  ×
                </button>
              </Tooltip>
            </span>
          )}
        </div>
        {!isCollapsed && (n.children.length > 0 || count > 0) && (
          <ul className={styles.treeRoot}>
            {n.children.map(renderNode)}
            {/* Osoby przypisane do komórki — pod komórkami podrzędnymi, jak w Magistracie */}
            {nodeMembers.map((m) => (
              <li key={m.Id}>
                <div
                  className={`${styles.nodeRow} ${styles.personRow}`}
                  style={{ paddingLeft: 6 + (n.depth + 1) * 18 }}
                  onClick={() => {
                    setSelectedId(n.Id);
                    setEditing(null);
                    setMemberToAssign(null);
                  }}
                >
                  <span className={styles.personIcon}>●</span>
                  <span className={styles.nodeName}>
                    {m.Label}
                    {m.UserId && m.UserId === n.ManagerUserId && (
                      <span className={styles.managerChip}>kierownik</span>
                    )}
                    {m.PositionTitle && (
                      <span className={styles.countChip}>
                        {m.PositionTitle}
                      </span>
                    )}
                    {m.Status === 1 && (
                      <span className={styles.countChip}>(zaproszony)</span>
                    )}
                  </span>
                  {canEditMembers && (
                    <span className={styles.nodeActions}>
                      <Tooltip title="Odepnij od komórki">
                        <button
                          className={styles.removeBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            assignMember(m, null);
                          }}
                        >
                          ×
                        </button>
                      </Tooltip>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.hint}>
        Komórki organizacyjne firmy i przypisani do nich członkowie zespołu.
        Symbol komórki trafia do sygnatury teczki (SYMBOL.KOD.NR.ROK), kierownik
        komórki jest „bezpośrednim przełożonym" w procedurach akceptacji.
        {unassigned.length > 0 && (
          <>
            {" "}
            Bez komórki: <strong>{unassigned.length}</strong>{" "}
            {unassigned.length === 1 ? "osoba" : "osób"}.
          </>
        )}
      </div>

      <div className={styles.contentArea}>
        <div className={styles.tree}>
          {tree.length === 0 ? (
            <div className={styles.empty}>Brak komórek organizacyjnych.</div>
          ) : (
            <ul className={styles.treeRoot}>{tree.map(renderNode)}</ul>
          )}
          {canEditStructure && (
            <button
              className={styles.addRootBtn}
              onClick={() => startCreate(null)}
            >
              + Nowy wydział
            </button>
          )}

          {unassigned.length > 0 && (
            <div className={styles.unassigned}>
              <div className={styles.unassignedTitle}>Osoby bez komórki</div>
              {unassigned.map((m) => (
                <div key={m.Id} className={styles.memberRow}>
                  <span className={styles.memberName}>{m.Label}</span>
                  {m.PositionTitle && (
                    <span className={styles.memberMeta}>{m.PositionTitle}</span>
                  )}
                  {canEditMembers && selected && (
                    <button
                      className={styles.linkBtn}
                      onClick={() => assignMember(m, selected.Id)}
                    >
                      → {selected.Name}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {editing ? (
          <div className={styles.nodeCard}>
            <div className={styles.nodeCardHeader}>
              <span className={styles.nodeCardLabel}>
                {editing.id ? "Edycja komórki" : "Nowa komórka"}
              </span>
              <button
                className={styles.nodeCardClose}
                onClick={() => setEditing(null)}
              >
                ✕
              </button>
            </div>
            <div className={styles.nodeCardBody}>
              {editing.parentId && (
                <div className={styles.memberMeta}>
                  Nadrzędna: {flat.find((n) => n.Id === editing.parentId)?.Name}
                </div>
              )}
              <TextField
                label="Nazwa komórki *"
                size="small"
                fullWidth
                autoFocus
                value={editing.name}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                label="Symbol (do sygnatury)"
                size="small"
                fullWidth
                value={editing.symbol}
                onChange={(e) =>
                  setEditing({ ...editing, symbol: e.target.value })
                }
                inputProps={{ maxLength: 20 }}
                helperText="np. OR, USC — pusty symbol dziedziczy z komórki nadrzędnej"
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Kierownik komórki</InputLabel>
                <Select
                  label="Kierownik komórki"
                  value={editing.managerUserId}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      managerUserId: e.target.value as string,
                    })
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {/* Kierownik wskazujący na członka usuniętego/odrzuconego: jawna pozycja,
                      żeby Select nie był poza zakresem (pusty) i było widać, że trzeba go zmienić. */}
                  {editing.managerUserId &&
                    !members.some(
                      (m) => m.UserId === editing.managerUserId
                    ) && (
                      <MenuItem value={editing.managerUserId}>
                        <em>
                          {(workspace?.Members ?? []).find(
                            (m) => m.UserId === editing.managerUserId
                          )?.Label ?? "Nieznany użytkownik"}{" "}
                          (nieaktywny — zmień kierownika)
                        </em>
                      </MenuItem>
                    )}
                  {members
                    .filter((m) => !!m.UserId)
                    .map((m) => (
                      <MenuItem key={m.UserId} value={m.UserId}>
                        {m.Label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <div className={styles.cardActions}>
                <button
                  className={styles.confirmBtn}
                  disabled={!editing.name.trim() || isCreating || isUpdating}
                  onClick={saveUnit}
                >
                  {editing.id ? "Zapisz" : "Utwórz"}
                </button>
              </div>
            </div>
          </div>
        ) : selected ? (
          <div className={styles.nodeCard}>
            <div className={styles.nodeCardHeader}>
              <span className={styles.nodeCardLabel}>
                {selected.Symbol ? `${selected.Symbol} · ` : ""}
                {selected.Name}
              </span>
              <button
                className={styles.nodeCardClose}
                onClick={() => setSelectedId(null)}
              >
                ✕
              </button>
            </div>
            <div className={styles.nodeCardBody}>
              <div className={styles.memberMeta}>
                Kierownik: {memberLabel(selected.ManagerUserId) ?? "—"}
              </div>

              {canEditStructure && flat.length > 1 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Komórka nadrzędna</InputLabel>
                  <Select
                    label="Komórka nadrzędna"
                    value={selected.ParentId ?? ""}
                    onChange={(e) =>
                      handleMoveUnit(
                        selected,
                        (e.target.value as string) || null
                      )
                    }
                  >
                    <MenuItem value="">— (najwyższy poziom)</MenuItem>
                    {flat
                      .filter((n) => !descendantIds(selected).has(n.Id))
                      .map((n) => (
                        <MenuItem key={n.Id} value={n.Id}>
                          {" ".repeat(n.depth * 2)}
                          {n.Name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              <div>
                <div className={styles.sectionLabel}>
                  Osoby ({unitMembers.length})
                </div>
                {unitMembers.length === 0 && (
                  <div className={styles.memberMeta}>
                    Brak przypisanych osób.
                  </div>
                )}
                {unitMembers.map((m) => (
                  <div key={m.Id} className={styles.memberRow}>
                    <div className={styles.memberMain}>
                      <span className={styles.memberName}>
                        {m.Label}
                        {m.UserId && m.UserId === selected.ManagerUserId
                          ? " (kierownik)"
                          : ""}
                      </span>
                      {positionEdit?.memberId === m.Id ? (
                        <span className={styles.inlineEdit}>
                          <TextField
                            size="small"
                            value={positionEdit.value}
                            placeholder="Stanowisko"
                            autoFocus
                            onChange={(e) =>
                              setPositionEdit({
                                memberId: m.Id,
                                value: e.target.value,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") savePosition();
                              if (e.key === "Escape") setPositionEdit(null);
                            }}
                            inputProps={{ maxLength: 200 }}
                          />
                          <button
                            className={styles.confirmBtn}
                            onClick={savePosition}
                          >
                            ✓
                          </button>
                        </span>
                      ) : (
                        <span
                          className={`${styles.memberMeta} ${
                            canEditMembers ? styles.editable : ""
                          }`}
                          title={
                            canEditMembers ? "Zmień stanowisko" : undefined
                          }
                          onClick={() =>
                            canEditMembers &&
                            setPositionEdit({
                              memberId: m.Id,
                              value: m.PositionTitle ?? "",
                            })
                          }
                        >
                          {m.PositionTitle ||
                            (canEditMembers ? "+ stanowisko" : "—")}
                        </span>
                      )}
                    </div>
                    {canEditMembers && (
                      <Tooltip title="Odepnij od komórki">
                        <button
                          className={styles.removeBtn}
                          onClick={() => assignMember(m, null)}
                        >
                          ×
                        </button>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>

              {canEditMembers && (
                <div>
                  <div className={styles.sectionLabel}>Przypisz osobę</div>
                  <Autocomplete<IWorkspaceMember>
                    options={members.filter(
                      (m) => m.StructureId !== selected.Id
                    )}
                    value={memberToAssign}
                    getOptionLabel={(m) =>
                      m.PositionTitle
                        ? `${m.Label} — ${m.PositionTitle}`
                        : m.Label
                    }
                    isOptionEqualToValue={(a, b) => a.Id === b.Id}
                    onChange={(_, v) => setMemberToAssign(v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Członek zespołu…"
                      />
                    )}
                    noOptionsText="Wszyscy członkowie są już w tej komórce"
                  />
                  <div className={styles.cardActions}>
                    <button
                      className={styles.confirmBtn}
                      disabled={!memberToAssign}
                      onClick={() =>
                        memberToAssign &&
                        assignMember(memberToAssign, selected.Id)
                      }
                    >
                      Przypisz do komórki
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.nodeCardEmpty}>
            <span>
              Zaznacz komórkę, aby zobaczyć i przypisać osoby
              {canEditStructure ? " albo ją edytować" : ""}.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StructureEditor;
